import { useEffect, useState } from "react";
import { Card, Row, Col, InputGroup, InputGroupText, Input, Button, Spinner } from "reactstrap";
import { AutoSizer, Table, Column, SortDirection } from "react-virtualized";
import { BsBarChartFill, BsSearch } from "react-icons/bs";
import { FaFilter } from "react-icons/fa";
import PageHeader from "../../sharedComponents/Header/PageHeader";
import MobileMenuSlideDrawer from "../../sharedComponents/SlideDrawer/MobileMenuSlideDrawer";
import { Theme, usStatesAndTerritoriesOptions } from "../../config/constants";
import { IoMdInformationCircle } from "react-icons/io";
import { InputLabel } from "@mui/material";
import { getLastCompletedQuarterDates } from "../../utils/dateUtils";
import LocalStorage from "../../sharedComponents/LocalStorage/LocalStorage";
import datasuiteApi from "../../api/datasuiteApi";
import { toast } from "react-toastify";
import useDataSuite from "../DataSuite/useDataSuite";
import { useDispatch, useSelector } from "react-redux";
import EmptyDataMessage from "../../sharedComponents/ErrorMessages/EmptyDataMessage";
import formatters from "../../config/formatters";
import { sortableHeaderRenderer } from "../../sharedComponents/Tables/SortableHeaderRenderer";
import { Tooltip } from "@mui/material";
import moment from "moment";
import { downloadCsvFile } from "../../config/dataManipulations";
import ShareOpportunityModal from "./Modals/ShareOpportunityModal";
import { showModal } from "../../reducers/ModalsReducer/ModalsActions";
import DateRangePicker from "../../sharedComponents/DateRangePicker/DateRangePicker";
import SelectAllSearch from "../../sharedComponents/Select/SelectAllSearch";

const MatchOpportunityIdentifier = () => {
    const dispatch = useDispatch();
    const chainData = LocalStorage.userData.get().chainData;
    const emergingData = LocalStorage.userData.get().emergingData;
    const mfrId = LocalStorage.manufacturerData.get().platform_id;
    const allFilters = useSelector(({datasuite}) => datasuite.allFilters);
    const { getFilters } = useDataSuite.useDataSuite();
    const { numberFormatter } = formatters();
    const infoString = "Our Match Opportunity Identifier algorithm compares your products to competitors using Category, Name, and Pack Size. If no match is found, you’ll see it as an Opportunity in “Growth Opportunities” but not the item it matches.";
    const tableString = "These are matched items for the selected category.";
    const [startDate, endDate] = getLastCompletedQuarterDates();
    const [inputText, setInputText] = useState('');
    const [selectedDateRange, setSelectedDateRange] = useState({startDate: startDate, endDate:endDate});
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [selectedSegment, setSelectedSegment] = useState(null);
    const [selectedOperator, setSelectedOperator] = useState(null);
    const [selectedState, setSelectedState] = useState(null);
    const [itemMatchData, setItemMatchData] = useState(null);
    const [filterLoading, setFilterLoading] = useState(true);
    const [dataLoading, setDataLoading] = useState(true);
    const [sortBy, setSortBy] = useState('opportunity_cases');
    const [sortDirection, setSortDirection] = useState(SortDirection.DESC);
    const [categoryMap, setCategoryMap] = useState([]);
    const [segmentMap, setSegmentMap] = useState([]);
    const [operatorMap, setOperatorMap] = useState([]);
    const [stateMap, setStateMap] = useState([]);

    const sortedItemMatchData = (() => {
        if (!itemMatchData) return [];
        const sorted = [...itemMatchData];
        switch (sortBy) {
            case 'location_name' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.location_name.localeCompare(b.location_name));
                    } else {
                        return (b.location_name.localeCompare(a.location_name));
                    }
                });
                break;
            }
            case 'match_item_description' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.match_item_description.localeCompare(b.match_item_description));
                    } else {
                        return (b.match_item_description.localeCompare(a.match_item_description));
                    }
                });
                break;
            }
            case 'category' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.category.localeCompare(b.category));
                    } else {
                        return (b.category.localeCompare(a.category));
                    }
                });
                break;
            }
            case 'your_cases' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.your_cases) - (b.your_cases);
                    } else {
                        return (b.your_cases) - (a.your_cases);
                    }
                });
                break;
            }
            case 'opportunity_cases' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.opportunity_cases) - (b.opportunity_cases);
                    } else {
                        return (b.opportunity_cases) - (a.opportunity_cases);
                    }
                });
                break;
            }
            case 'total_cases' : {
                sorted.sort((a, b) => {
                    if (sortDirection === SortDirection.ASC) {
                        return (a.total_cases) - (b.total_cases);
                    } else {
                        return (b.total_cases) - (a.total_cases);
                    }
                });
                break;
            }
            default: {
                // Do nothing
            }
        }
        return sorted;
    })();

    const searchedItemMatchData = (() => {
        if (sortedItemMatchData?.length <= 0 || inputText === null || inputText === '') return [];
        const initialData = [...sortedItemMatchData];
        const inputTextLowercase = inputText.toLowerCase()
        const searched = initialData.filter((rowData) => {
            return rowData.category.toLowerCase().includes(inputTextLowercase)
            || rowData.match_item_description.toLowerCase().includes(inputTextLowercase)
            || rowData.location_name.toLowerCase().includes(inputTextLowercase);    
        });
        return searched;
    })();

    const getMatchOpportunityItems = () => {
        const params = {
            manufacturerPlatformId: mfrId,
            startDate: selectedDateRange.startDate,
            endDate: selectedDateRange.endDate,
            chainData: chainData,
            emergingData: emergingData,
            categories: selectedCategory?.map((category) => category?.value || '') || [],
            distributors: [],
            locations: selectedOperator?.map((operator) => operator?.value || '') || [],
            segments: selectedSegment?.map((segment) => segment?.value || '') || [],
            states: selectedState?.map((state) => state?.value || '') || []
        };
        datasuiteApi.matchOpportunityIdentifier.post(params).then((response) => {
            setItemMatchData(response.data);
            setDataLoading(false);
        }).catch((err) => {
            console.log(err);
            toast.error(err.message);
            setDataLoading(false);
        });
    };

    const setFilters = () => {
        const categoryMap =  allFilters?.categories?.map((category) => {
            return (
                {value: category, label: category}
            );
        });
        setCategoryMap(categoryMap);
        const segmentMap =  allFilters?.segments?.map((segment) => {
            const segmentLabel = segment.split('_').map(w => w[0].toUpperCase() + w.substring(1).toLowerCase()).join(' ');
            return (
                {value: segment, label: segmentLabel}
            );
        }); 
        setSegmentMap(segmentMap);  
        const operatorMap =  allFilters?.locations?.map((location) => {
            return (
                {value: location, label: location}
            );
        });
        setOperatorMap(operatorMap);
        const stateMap = usStatesAndTerritoriesOptions.slice();
        setStateMap(stateMap);
    };

    const handleCategoryChange = (category) => {
        if (category?.length === 0) {
            setSelectedCategory(null);
        } else {
            setSelectedCategory(category);
        }
    };

    const handleSegmentChange = (segment) => {
        if (segment?.length === 0) {
            setSelectedSegment(null);
        } else {
            setSelectedSegment(segment);
        }
    };

    const handleOperatorChange = (operator) => {
        if (operator?.length === 0) {
            setSelectedOperator(null);
        } else {
            setSelectedOperator(operator);
        }
    };

    const handleStateChange = (state) => {
        if (state?.length === 0) {
            setSelectedState(null);
        } else {
            setSelectedState(state);
        }
    };

    const onClickSearch = () => {
        setDataLoading(true);
        getMatchOpportunityItems();
    };

    const onClickExport = () => {
        const filename = "BEP_Match_Opportunities_" + moment().format('YYYYMMDD_hhmmss').toString();
        downloadCsvFile(sortedItemMatchData, filename);
        toast.success('Data successfully exported to downloads folder.')
    };

    const onClickShareReport = () => {
        const modalParams = {
            selectedCategory: selectedCategory,
            selectedOperator: selectedOperator,
            selectedSegment: selectedSegment,
            selectedState: selectedState
        };
        dispatch(showModal(ShareOpportunityModal, modalParams));
    };

    useEffect(() => {
        getFilters();
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        setFilters();
        // eslint-disable-next-line
    }, [allFilters])

    useEffect(() => {
        if (categoryMap?.length > 0 && !selectedCategory) {
            setSelectedCategory([categoryMap[1]]);
        }
        // eslint-disable-next-line
    }, [categoryMap]);

    useEffect(() => {
        if (filterLoading === true && selectedCategory) {
            getMatchOpportunityItems();
            setFilterLoading(false);
        }
        //eslint-disable-next-line
    }, [selectedCategory]);

    return (
        <>
            <PageHeader title='SalesStream' subtitle='Match Opportunity Identifier' icon={<BsBarChartFill fill='url(#icon-gradient)'/>}/>
            <div className='bg-transparent p-3 matchOpportunityIdentifierContainer'>
                <Card style={{minHeight: '56px', background: Theme().colors.accent}}>
                    <Col className="w-100">
                        <Row className="w-100 h-100 m-auto" style={{alignItems: 'center'}}>
                            <Col className="w-fc">
                                <IoMdInformationCircle style={{fontSize: '20px', color: 'white'}} />
                            </Col>
                            <Col style={{fontWeight: '600', color: 'white'}}>
                                {infoString}
                            </Col>
                        </Row>
                    </Col>
                </Card>
                <Card className='px-3 py-2' style={{background:'url("./FiltersBackground.png")', backgroundRepeat: 'no-repeat', backgroundSize: 'cover', minHeight: '112px'}}>
                    <Col>
                        <Row className='mb-2'>
                            <div className="main-header text-size-20" style={{color: '#00A6CC'}}><FaFilter fontSize={'15px'} style={{marginBottom: '5px'}}/> Filters</div>
                        </Row>
                        <Row>
                            <Col style={{minWidth: '200px'}}>
                                <InputLabel style={{color: 'black', fontSize: '12px'}}>Select Date Range</InputLabel>
                                <DateRangePicker 
                                id='dashboardDateSearch'
                                defaultStartDate={moment(startDate).format('MM/DD/YYYY')} 
                                defaultEndDate={moment(endDate).format('MM/DD/YYYY')} 
                                minDate={moment(startDate).format('MM/DD/YYYY')} 
                                maxDate={moment().format('MM/DD/YYYY')} 
                                callback={(start, end) => setSelectedDateRange({startDate: start, endDate: end})}
                                ranges={{
                                    'Last 30 Days': [moment().subtract(29, 'days'), moment()],
                                    'Last 2 Months': [moment().subtract(2, 'month'), moment()],
                                    'Last Year': [moment().subtract(1, 'year'), moment()]
                                    }
                                }
                                />
                            </Col>
                            <Col style={{minWidth: '200px'}}>
                                <InputLabel style={{color: 'black', fontSize: '12px'}}>Category</InputLabel>
                                <SelectAllSearch
                                    placeholder='Select Category'
                                    isMulti={true}
                                    closeMenuOnSelect={false}
                                    isLoading={filterLoading}
                                    options={categoryMap}
                                    value={selectedCategory}
                                    onChange={(category) => handleCategoryChange(category)}
                                />
                            </Col>
                            <Col style={{minWidth: '200px'}}>
                                <InputLabel style={{color: 'black', fontSize: '12px'}}>Customer Segment</InputLabel>
                                <SelectAllSearch
                                    placeholder='Select Segment'
                                    isMulti={true}
                                    closeMenuOnSelect={false}
                                    isLoading={filterLoading}
                                    options={segmentMap}
                                    value={selectedSegment}
                                    onChange={(segment) => handleSegmentChange(segment)}
                                />
                            </Col>
                            <Col style={{minWidth: '200px'}}>
                                <InputLabel style={{color: 'black', fontSize: '12px'}}>Operator</InputLabel>
                                <SelectAllSearch
                                    placeholder='Select Operator'
                                    isMulti={true}
                                    closeMenuOnSelect={false}
                                    isLoading={filterLoading}
                                    options={operatorMap}
                                    value={selectedOperator}
                                    onChange={(operator) => handleOperatorChange(operator)}
                                />
                            </Col>
                            <Col style={{minWidth: '200px'}}>
                                <InputLabel style={{color: 'black', fontSize: '12px'}}>State</InputLabel>
                                <SelectAllSearch
                                    placeholder='Select State'
                                    isMulti={true}
                                    closeMenuOnSelect={false}
                                    isLoading={filterLoading}
                                    options={stateMap}
                                    value={selectedState}
                                    onChange={(state) => handleStateChange(state)}
                                />
                            </Col>
                            <Col className="w-fc">
                                <div style={{height: '16px'}}></div>
                                <Button
                                    block 
                                    className='py-2 px-3 button-label'
                                    size='sm'
                                    style={{width: '80px', color: 'white'}}
                                    color={'blueFilter'}
                                    onClick={() => onClickSearch()}>Apply</Button>
                            </Col>
                        </Row>
                    </Col>
                </Card>
                <Card className="px-2 py-3" style={{height: 'calc(100vh - 140px)'}}>
                    <Col style={{display: 'flex', flexDirection: 'column'}}>
                        <Row className="w-100 mx-auto pe-0" style={{paddingBottom: '12px'}}>
                            <Col className="main-header text-size-18 w-fc" style={{alignContent: 'center'}}>
                                <span>Match Opportunities</span>
                            </Col>
                            <Col className="w-fc ps-0" style={{alignContent: 'center'}}>
                                <Tooltip title={tableString} placement="top" arrow>
                                    <div style={{position: 'relative', bottom: '2px'}}>
                                        <IoMdInformationCircle color={Theme().colors.accent} style={{fontSize: "18px"}} />
                                    </div>
                                </Tooltip>
                            </Col>
                            <Col></Col>
                            <Col className="w-fc">
                                <Button
                                    outline 
                                    className='py-1 button-label'
                                    size='sm'
                                    style={{width: '80px'}}
                                    disabled={dataLoading}
                                    color={Theme().colors.accentName}
                                    onClick={() => onClickShareReport()}>Share</Button>
                            </Col>
                            <Col className="w-fc">
                                <Button
                                    outline
                                    className='py-1 button-label' 
                                    size='sm'
                                    style={{width: '80px'}}
                                    disabled={dataLoading}
                                    color={Theme().colors.accentName}
                                    onClick={onClickExport}>Export</Button>
                            </Col>
                        </Row>
                        <Row>
                            <Col>
                                <InputGroup>
                                    <InputGroupText>
                                        <BsSearch/>
                                    </InputGroupText>
                                    <Input className='text-input' value={inputText} onChange={(e) => setInputText(e.target.value)} placeholder='Search...'/>
                                </InputGroup>
                            </Col>
                        </Row>
                        <Row style={{paddingTop: '8px', flex: '1'}}>
                            {(itemMatchData?.length <= 0 || itemMatchData == null) && !dataLoading &&
                            <Col className="m-auto">
                                <EmptyDataMessage />
                                <div style={{textAlign: 'center'}}>Try broadening your search (at least one Category is required).</div>
                            </Col>
                            }
                            {dataLoading &&
                            <Col className="m-auto">
                                <div className="mx-auto my-auto" style={{height:"2.5%", width:"2.5%"}}>
                                    <Spinner animation="border" />
                                </div>
                            </Col>
                            }
                            {itemMatchData?.length > 0 && !dataLoading &&
                            <Col>
                                <AutoSizer>
                                    {({width, height}) => (
                                        <Table className="table totalOpportunitiesTable border-grayLight" id="totalOpportunitiesTable"
                                            width={width}
                                            height={height}
                                            headerHeight={34.5}
                                            rowHeight={38}
                                            rowCount={(searchedItemMatchData.length > 0 || inputText !== '') ? searchedItemMatchData?.length : sortedItemMatchData?.length}
                                            rowGetter={({ index }) => (searchedItemMatchData.length > 0 || inputText !== '') ? searchedItemMatchData?.[index] : sortedItemMatchData?.[index]}
                                            sort={({ sortBy: sb, sortDirection: sd }) => {
                                                setSortBy(sb);
                                                setSortDirection(sd);
                                            }}
                                            sortBy={sortBy}
                                            sortDirection={sortDirection}
                                        >
                                            <Column
                                            label='Parent Group'
                                            dataKey='location_name'
                                            width={width * 0.13}
                                            headerRenderer={sortableHeaderRenderer}
                                            />
                                            <Column
                                            label='MIN'
                                            dataKey='match_item_min'
                                            width={width * 0.05}
                                            />
                                            <Column
                                            label='Description'
                                            dataKey='match_item_description'
                                            width={width * 0.15}
                                            headerRenderer={sortableHeaderRenderer}
                                            />
                                            <Column
                                            label='Category'
                                            dataKey='category'
                                            width={width * 0.12}
                                            headerRenderer={sortableHeaderRenderer}
                                            />
                                            <Column
                                            label='Pack Size'
                                            dataKey='match_item_pack'
                                            width={width * 0.1}
                                            />
                                            <Column
                                            label='Your Cases'
                                            dataKey='your_cases'
                                            width={width * 0.1}
                                            cellRenderer={({ cellData }) => <span>{numberFormatter.format(Math.round(cellData))}</span>}
                                            headerRenderer={sortableHeaderRenderer}
                                            />
                                            <Column
                                            label='Opportunity Cases'
                                            dataKey='opportunity_cases'
                                            width={width * 0.15}
                                            cellRenderer={({ cellData }) => <span>{numberFormatter.format(Math.round(cellData))}</span>}
                                            headerRenderer={sortableHeaderRenderer}
                                            />
                                            <Column
                                            label='Total Cases'
                                            dataKey='total_cases'
                                            width={width * 0.1}
                                            cellRenderer={({ cellData }) => <span>{numberFormatter.format(Math.round(cellData))}</span>}
                                            headerRenderer={sortableHeaderRenderer}
                                            />
                                        </Table>
                                    )}
                                    </AutoSizer>
                            </Col>
                            }
                        </Row>
                    </Col>
                </Card>
            </div>
            <MobileMenuSlideDrawer />
        </>
        
    )
}

export default MatchOpportunityIdentifier;